/**
 * Production View (Unified)
 *
 * Streamlined production floor interface optimized for:
 * - Single work center focus
 * - Large touch targets (48px minimum)
 * - Prominent active job display
 * - Minimal cognitive load
 *
 * Design principles:
 * 1. One primary action visible at all times
 * 2. Active work is front and center
 * 3. Job queue below for quick switching
 */

import { useState, useEffect, useCallback, useMemo } from "react";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Work centers for production floor
const WORK_CENTERS = [
  { id: "PA", label: "Prod Art", color: "blue" },
  { id: "OPTI", label: "OPTI", color: "purple" },
  { id: "ASSEMBLY", label: "Assembly", color: "amber" },
  { id: "PAINT", label: "Paint", color: "emerald" },
  { id: "3D MODEL", label: "3D Model", color: "cyan" },
];

export default function ProductionView({ onBackToApp, userEmail }) {
  // Single work center selection
  const [workCenter, setWorkCenter] = useState(() => {
    return localStorage.getItem("3dcolor-production-wc") || "PA";
  });

  // Jobs data
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active job tracking
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);

  // UI state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [piecesFinished, setPiecesFinished] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);

  // Timer tick for live updates
  const [tick, setTick] = useState(0);

  const getToken = () => sessionStorage.getItem("msal.backendToken");

  // Persist work center
  useEffect(() => {
    localStorage.setItem("3dcolor-production-wc", workCenter);
  }, [workCenter]);

  // Timer for live updates
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch jobs for work center
  const fetchJobs = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/production/my-queue?workCenters=${encodeURIComponent(workCenter)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);

      const data = await res.json();

      // Flatten jobs from quotes
      const allJobs = [];
      (data.quotes || []).forEach((quote) => {
        (quote.jobs || []).forEach((job) => {
          if (job.myOperations && job.myOperations.length > 0) {
            allJobs.push({
              ...job,
              quoteNumber: quote.quoteNumber,
              projectName: quote.projectName,
              dueDate: quote.dueDate,
              customerCode: quote.customerCode,
            });
          }
        });
      });

      // Sort: active first, then by due date
      allJobs.sort((a, b) => {
        if (a.activeSession && !b.activeSession) return -1;
        if (!a.activeSession && b.activeSession) return 1;
        const dateA = a.dueDate ? new Date(a.dueDate) : new Date("9999-12-31");
        const dateB = b.dueDate ? new Date(b.dueDate) : new Date("9999-12-31");
        return dateA - dateB;
      });

      setJobs(allJobs);

      // Track active session
      const active = allJobs.find((j) => j.activeSession);
      if (active) {
        setActiveJobId(active.jobId);
        setActiveSession(active.activeSession);
      } else {
        setActiveJobId(null);
        setActiveSession(null);
      }

      setError(null);
    } catch (err) {
      console.error("Production fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workCenter]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Time tracking actions
  const handleStart = async (job) => {
    const token = getToken();
    if (!token || !job) return;

    const opCode = job.myOperations[0]?.operationCode || workCenter;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${job.jobId}/time/start`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operationCode: opCode,
          workCenter: workCenter,
          orderNumber: job.orderNumber,
          quoteNumber: job.quoteNumber,
          jobNumber: job.jobNumber,
          jobName: job.description,
        }),
      });

      if (!res.ok) throw new Error("Failed to start");
      await fetchJobs();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Start error:", err);
      alert("Failed to start job");
    }
  };

  const handlePause = async () => {
    const token = getToken();
    if (!token || !activeJobId) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${activeJobId}/time/pause`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to pause");
      await fetchJobs();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Pause error:", err);
    }
  };

  const handleResume = async () => {
    const token = getToken();
    if (!token || !activeJobId) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${activeJobId}/time/resume`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to resume");
      await fetchJobs();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Resume error:", err);
    }
  };

  const handleComplete = async () => {
    const token = getToken();
    if (!token || !activeJobId) return;

    try {
      const pieces = parseInt(piecesFinished, 10) || 0;
      const res = await fetch(`${API_BASE}/api/jobs/${activeJobId}/time/complete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ piecesFinished: pieces }),
      });

      if (!res.ok) throw new Error("Failed to complete");

      setShowCompleteModal(false);
      setPiecesFinished("");
      await fetchJobs();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Complete error:", err);
    }
  };

  // Format elapsed time
  const formatElapsed = useCallback((session) => {
    if (!session) return "0:00";

    const accumulated = Math.floor(session.accumulatedSeconds || 0);
    let seconds = 0;

    if (session.state === "running" && session.startTime) {
      const startMs = new Date(session.startTime).getTime();
      const currentSegment = Math.floor((Date.now() - startMs) / 1000);
      seconds = accumulated + currentSegment;
    } else {
      seconds = accumulated;
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  }, []);

  // Format due date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Check if job is due soon (within 2 days)
  const isDueSoon = (dateStr) => {
    if (!dateStr) return false;
    const due = new Date(dateStr);
    const now = new Date();
    const diffDays = (due - now) / (1000 * 60 * 60 * 24);
    return diffDays <= 2 && diffDays >= 0;
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Get active job data
  const activeJob = useMemo(() => {
    return jobs.find((j) => j.jobId === activeJobId);
  }, [jobs, activeJobId]);

  const isRunning = activeSession?.state === "running";
  const isPaused = activeSession?.state === "paused";

  // Force re-render on tick
  void tick;

  return (
    <div className="flex-1 overflow-auto bg-white text-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-100 border-b border-gray-200">
        {/* Work Center Tabs */}
        <div className="flex border-t border-gray-200">
          {WORK_CENTERS.map((wc) => (
            <button
              key={wc.id}
              onClick={() => setWorkCenter(wc.id)}
              className={`flex-1 py-4 text-center font-medium transition-colors ${
                workCenter === wc.id
                  ? "bg-indigo-600 text-gray-900"
                  : "text-gray-500 hover:bg-slate-100 hover:text-gray-900"
              }`}
            >
              {wc.label}
            </button>
          ))}
        </div>
      </header>

      {/* Active Job Display */}
      {activeJob && (
        <div className={`p-4 ${isRunning ? "bg-green-50" : "bg-yellow-900/30"}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-sm text-gray-500">
                  {activeJob.jobNumber}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs rounded-full ${
                    isRunning
                      ? "bg-green-600 text-gray-900"
                      : "bg-yellow-600 text-gray-900"
                  }`}
                >
                  {isRunning ? "Running" : "Paused"}
                </span>
              </div>
              <div className="text-lg font-semibold truncate">
                {activeJob.description || activeJob.partNumber || "No description"}
              </div>
              <div className="text-sm text-gray-500">
                {activeJob.projectName}
              </div>
            </div>
            <div className="text-right">
              <div
                className={`text-4xl font-mono font-bold ${
                  isRunning ? "text-green-400" : "text-yellow-400"
                }`}
              >
                {formatElapsed(activeSession)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Qty: {activeJob.qty || 0}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {isRunning ? (
              <button
                onClick={handlePause}
                className="flex-1 py-4 bg-yellow-600 hover:bg-yellow-700 text-gray-900 text-lg font-bold rounded-xl transition-colors"
              >
                PAUSE
              </button>
            ) : (
              <button
                onClick={handleResume}
                className="flex-1 py-4 bg-green-600 hover:bg-green-700 text-gray-900 text-lg font-bold rounded-xl transition-colors"
              >
                RESUME
              </button>
            )}
            <button
              onClick={() => setShowCompleteModal(true)}
              className="flex-1 py-4 bg-blue-600 hover:bg-blue-700 text-gray-900 text-lg font-bold rounded-xl transition-colors"
            >
              DONE
            </button>
          </div>
        </div>
      )}

      {/* Job Queue */}
      <main className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
          </div>
        ) : error ? (
          <div className="p-4">
            <div className="bg-red-100 border border-red-700 rounded-lg p-4 text-center">
              <p className="text-red-600 mb-3">{error}</p>
              <button
                onClick={fetchJobs}
                className="px-4 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <svg
              className="h-16 w-16 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-lg">No jobs for {workCenter}</p>
            <p className="text-sm mt-1">All caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200/50">
            {/* Queue Header */}
            <div className="px-4 py-2 bg-slate-100 sticky top-0">
              <span className="text-sm text-gray-500">
                {jobs.length} job{jobs.length !== 1 ? "s" : ""} in queue
              </span>
            </div>

            {/* Job Cards */}
            {jobs
              .filter((j) => j.jobId !== activeJobId)
              .map((job) => (
                <div
                  key={job.jobId}
                  className="bg-slate-200 hover:bg-slate-100 transition-colors"
                >
                  <div className="p-4 flex items-center gap-4">
                    {/* Job Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-gray-500">
                          {job.jobNumber}
                        </span>
                        {job.qty && (
                          <span className="text-xs text-yellow-400 font-medium">
                            x{job.qty}
                          </span>
                        )}
                        {isOverdue(job.dueDate) && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-red-600 text-gray-900 rounded">
                            LATE
                          </span>
                        )}
                        {isDueSoon(job.dueDate) && !isOverdue(job.dueDate) && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-orange-600 text-gray-900 rounded">
                            DUE SOON
                          </span>
                        )}
                      </div>
                      <div className="text-gray-900 truncate">
                        {job.description || job.partNumber || "No description"}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <span>{job.projectName}</span>
                        {job.dueDate && (
                          <>
                            <span>-</span>
                            <span
                              className={
                                isOverdue(job.dueDate)
                                  ? "text-red-400"
                                  : isDueSoon(job.dueDate)
                                  ? "text-orange-400"
                                  : ""
                              }
                            >
                              {formatDate(job.dueDate)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Start Button */}
                    <button
                      onClick={() => handleStart(job)}
                      disabled={!!activeJobId}
                      className={`px-6 py-3 rounded-xl font-bold transition-colors ${
                        activeJobId
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-gray-900"
                      }`}
                    >
                      START
                    </button>
                  </div>

                  {/* Expanded Details (operation info) */}
                  {expandedJob === job.jobId && (
                    <div className="px-4 pb-4 pt-0">
                      <div className="bg-white rounded-lg p-3 text-sm">
                        <div className="text-gray-500 mb-2">Operations:</div>
                        <div className="flex flex-wrap gap-2">
                          {job.myOperations.map((op, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-indigo-600/50 text-indigo-300 rounded text-xs"
                            >
                              {op.operationCode}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </main>

      {/* Complete Modal */}
      {showCompleteModal && activeJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-100 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Complete Job</h3>

            <div className="mb-6">
              <div className="text-gray-500 mb-1">{activeJob.jobNumber}</div>
              <div className="text-lg text-gray-900">
                {activeJob.description || activeJob.partNumber}
              </div>
              <div className="text-3xl font-mono font-bold text-green-400 mt-3">
                {formatElapsed(activeSession)}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">
                Pieces Finished
              </label>
              <input
                type="number"
                value={piecesFinished}
                onChange={(e) => setPiecesFinished(e.target.value)}
                placeholder={activeJob.qty?.toString() || "0"}
                className="w-full px-4 py-4 bg-white border border-gray-300 rounded-xl text-gray-900 text-xl text-center focus:outline-none focus:border-blue-500"
                autoFocus
              />
              <div className="text-sm text-gray-500 text-center mt-2">
                Expected: {activeJob.qty || 0}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setPiecesFinished("");
                }}
                className="flex-1 py-4 bg-gray-700 hover:bg-gray-600 text-gray-900 font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 py-4 bg-green-600 hover:bg-green-700 text-gray-900 font-bold rounded-xl transition-colors"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
